import React, { useState, useRef } from "react";
import { 
  EditOptionContainer, 
  EditOptionTitle, 
  EditOptionDescription, 
  FormGroup, 
  Button, 
  ButtonGroup,
  TipBox,
  RadioGroup
} from "../EditOptionStyles";
import { eraseElement } from "../../utils/editUtils";
import DrawingModal from "./DrawingModal";

const EraseElement = ({ onSubmit, isLoading, selectedImage }) => {
  const [maskMode, setMaskMode] = useState("draw");
  const [maskFile, setMaskFile] = useState(null);
  const [brushSize, setBrushSize] = useState(20);
  const [isDrawingModalOpen, setIsDrawingModalOpen] = useState(false);
  const [generatedMask, setGeneratedMask] = useState(null);
  const [maskPreviewUrl, setMaskPreviewUrl] = useState(null);
  
  const fileInputRef = useRef(null);
  
  const maskModeOptions = [
    { value: "draw", label: "Draw on Image" },
    { value: "upload", label: "Upload Mask" }
  ];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setMaskFile(file);
    }
  };

  const handleDrawingComplete = (maskBlob, previewDataUrl) => {
    setGeneratedMask(maskBlob);
    setMaskPreviewUrl(previewDataUrl || null);
  };

  const handleSubmit = async () => {
    let finalMaskFile = null;
    
    if (maskMode === "draw") {
      if (!generatedMask) {
        alert("Please draw areas to erase on the image first.");
        return;
      }
      finalMaskFile = generatedMask;
    } else {
      if (!maskFile) {
        alert("Please select a mask file to define the area to erase.");
        return;
      }
      finalMaskFile = maskFile;
    }

    // Structure options according to API documentation
    // Note: The main image will be passed as the first parameter to the API function
    // We only need to include mask and other parameters in options
    const apiOptions = {
      mask_file: finalMaskFile,
      mask_type: "manual" // Default as per API
    };

    console.log("EraseElement - Submitting with:");
    console.log("- Selected Image:", selectedImage);
    console.log("- Mask Mode:", maskMode);
    console.log("- Mask File/Blob:", finalMaskFile);
    console.log("- API Options:", apiOptions);

    onSubmit({
      editType: "eraseElement",
      options: apiOptions,
      apiFunction: eraseElement
    });
  };

  const canSubmit =
    Boolean(selectedImage) &&
    (maskMode === "draw" ? Boolean(generatedMask) : Boolean(maskFile));

  return (
    <>
      <EditOptionContainer>
        <div>
          <EditOptionTitle icon="🗑️">Eraser</EditOptionTitle>
          <EditOptionDescription>
            Remove elements or specific areas from your image by drawing on areas to erase or uploading a mask. White areas (255) will be erased, black areas (0) will remain.
          </EditOptionDescription>
        </div>

        <FormGroup label="Mask Creation Method">
          <RadioGroup 
            options={maskModeOptions} 
            value={maskMode} 
            onChange={setMaskMode}
          />
        </FormGroup>

        {maskMode === "draw" ? (
          <FormGroup label="Drawing Tool">
            <div style={{
              marginBottom: "1rem",
              padding: "1.5rem",
              backgroundColor: generatedMask ? "#f0f5ff" : "#f8fafd",
              borderRadius: "8px",
              border: `2px dashed ${generatedMask ? "#4D7CFE" : "#dde3ed"}`,
              textAlign: "center",
              cursor: selectedImage ? "pointer" : "not-allowed"
            }} onClick={() => selectedImage && setIsDrawingModalOpen(true)}>
              {selectedImage ? (
                <div>
                  {generatedMask && maskPreviewUrl ? (
                    <img
                      src={maskPreviewUrl}
                      alt="Mask preview"
                      style={{
                        width: "100%",
                        borderRadius: "4px",
                        marginBottom: "0.5rem",
                        display: "block"
                      }}
                    />
                  ) : (
                    <div style={{
                      fontSize: "2rem",
                      marginBottom: "0.5rem",
                      color: "#666"
                    }}>
                      🎨
                    </div>
                  )}
                  <div style={{
                    color: generatedMask ? "#4D7CFE" : "#666",
                    fontWeight: "500",
                    marginBottom: "0.5rem"
                  }}>
                    {generatedMask ? "Mask Created — click to modify" : "Open Drawing Tool"}
                  </div>
                  {!generatedMask && (
                    <div style={{ color: "#888", fontSize: "0.9rem" }}>
                      Click to start drawing areas to erase
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: "#666", padding: "1rem" }}>
                  Please select an image first to start drawing
                </div>
              )}
            </div>
            <div style={{ fontSize: "0.75rem", color: "#888" }}>
              Opens a full-screen drawing interface for precise mask creation
            </div>
          </FormGroup>
        ) : (
          <FormGroup label="Upload Mask">
            <div style={{
              marginBottom: "1rem",
              padding: "1.5rem",
              backgroundColor: maskFile ? "#f0f5ff" : "#f8fafd",
              borderRadius: "8px",
              border: `2px dashed ${maskFile ? "#4D7CFE" : "#dde3ed"}`,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s ease"
            }} onClick={() => fileInputRef.current?.click()}>
              {maskFile ? (
                <div>
                  <div style={{ color: "#4D7CFE", fontWeight: "500", marginBottom: "0.5rem" }}>
                    ✓ Mask Selected
                  </div>
                  <div style={{ color: "#666", fontSize: "0.9rem" }}>
                    {maskFile.name}
                  </div>
                  <div style={{ color: "#888", fontSize: "0.8rem", marginTop: "0.25rem" }}>
                    Click to change mask
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ color: "#666", fontWeight: "500", marginBottom: "0.5rem" }}>
                    Upload Binary Mask
                  </div>
                  <div style={{ color: "#888", fontSize: "0.9rem" }}>
                    White areas (255) will be erased, black areas (0) will remain
                  </div>
                </div>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/jpeg,image/jpg,image/png,image/webp"
              style={{ display: "none" }}
            />
            <div style={{ fontSize: "0.75rem", color: "#888" }}>
              Supported formats: JPEG, JPG, PNG, WEBP. Mask must have the same aspect ratio as the input image.
            </div>
          </FormGroup>
        )}

        <TipBox 
          title="How to Create Masks" 
          icon="💡"
        >
          You can create masks by drawing directly on the image or by using the /objects/mask_generator route to generate all possible masks for an image.
        </TipBox>

        <ButtonGroup>
          <Button 
            primary={false} 
            outline={true}
            onClick={() => {
              setMaskFile(null);
              setGeneratedMask(null);
              setMaskPreviewUrl(null);
              setBrushSize(20);
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
            }}
          >
            Reset
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? "Erasing..." : "Erase Element"}
          </Button>
        </ButtonGroup>
      </EditOptionContainer>

      {/* Full-screen Drawing Modal */}
      <DrawingModal
        isOpen={isDrawingModalOpen}
        onClose={() => setIsDrawingModalOpen(false)}
        selectedImage={selectedImage}
        brushSize={brushSize}
        setBrushSize={setBrushSize}
        onSave={handleDrawingComplete}
        title="Draw Areas to Erase"
        description="Paint over areas you want to remove. Hold Ctrl/Cmd to pan."
      />
    </>
  );
};

export default EraseElement;
